import { z } from 'zod';
import type { ImpactLevel } from './scenarios';

/**
 * Requirement #14 — Risk-Based Selection & Coverage Intelligence.
 * Deterministic, explainable scoring. Advisory only — never auto-executes.
 * Prefer composing existing signals; no vanity single coverage %.
 */

export const RISK_SELECTION_LEVELS = ['low', 'medium', 'high', 'critical'] as const;
export type RiskSelectionLevel = (typeof RISK_SELECTION_LEVELS)[number];

export const RISK_SELECTION_CONFIDENCE = ['low', 'medium', 'high'] as const;
export type RiskSelectionConfidence = (typeof RISK_SELECTION_CONFIDENCE)[number];

export const RISK_SELECTION_BUCKETS = ['required', 'recommended', 'optional', 'excluded'] as const;
export type RiskSelectionBucket = (typeof RISK_SELECTION_BUCKETS)[number];

export const RISK_SELECTION_TARGET_KINDS = ['flow', 'scenario', 'requirement'] as const;
export type RiskSelectionTargetKind = (typeof RISK_SELECTION_TARGET_KINDS)[number];

export const RISK_SELECTION_CONTEXTS = ['regression', 'release', 'change', 'adhoc'] as const;
export type RiskSelectionContext = (typeof RISK_SELECTION_CONTEXTS)[number];

export const RiskSelectionEvaluateRequestSchema = z.object({
  /** Optional commit SHA — uses latest change review / TIA when available. */
  commitSha: z.string().trim().min(7).max(64).optional(),
  flowIds: z.array(z.string().uuid()).max(200).optional(),
  scenarioIds: z.array(z.string().uuid()).max(200).optional(),
  requirementIds: z.array(z.string().uuid()).max(100).optional(),
  context: z.enum(RISK_SELECTION_CONTEXTS).optional().default('adhoc'),
  /** When true, include Quality-first scenario/requirement dimensions. Default true. */
  includeScenarios: z.boolean().optional().default(true),
  /** Automation-first always includes flows when available. */
  includeFlows: z.boolean().optional().default(true),
});
export type RiskSelectionEvaluateRequest = z.input<typeof RiskSelectionEvaluateRequestSchema>;

export const RiskReasonSchema = z.object({
  code: z.string().max(80),
  message: z.string().max(500),
  weight: z.number().min(0).max(100).optional(),
});
export type RiskReason = z.infer<typeof RiskReasonSchema>;

export const RiskSelectionItemSchema = z.object({
  kind: z.enum(RISK_SELECTION_TARGET_KINDS),
  id: z.string().uuid(),
  name: z.string(),
  bucket: z.enum(RISK_SELECTION_BUCKETS),
  riskLevel: z.enum(RISK_SELECTION_LEVELS),
  riskScore: z.number().min(0).max(100),
  confidence: z.enum(RISK_SELECTION_CONFIDENCE),
  reasons: z.array(RiskReasonSchema).max(20),
  /** Linked scenario when kind=flow and a mapping exists. */
  linkedScenarioIds: z.array(z.string().uuid()).max(20).optional(),
  linkedRequirementIds: z.array(z.string().uuid()).max(20).optional(),
  linkedFlowIds: z.array(z.string().uuid()).max(20).optional(),
});
export type RiskSelectionItem = z.infer<typeof RiskSelectionItemSchema>;

export const CoverageDimensionSchema = z.object({
  /** null = insufficient evidence — never invent a %. */
  ratio: z.number().min(0).max(1).nullable(),
  covered: z.number().int().nonnegative().nullable(),
  total: z.number().int().nonnegative().nullable(),
  unknown: z.boolean().default(false),
  note: z.string().max(300).optional(),
});
export type CoverageDimension = z.infer<typeof CoverageDimensionSchema>;

export const RiskCoverageDimensionsSchema = z.object({
  requirementCoverage: CoverageDimensionSchema,
  automationCoverage: CoverageDimensionSchema,
  changeCoverage: CoverageDimensionSchema,
  executionCoverage: CoverageDimensionSchema,
  maintenanceCoverage: CoverageDimensionSchema,
});
export type RiskCoverageDimensions = z.infer<typeof RiskCoverageDimensionsSchema>;

export const RiskCoverageGapSchema = z.object({
  kind: z.enum(['requirement_uncovered', 'scenario_manual_only', 'flow_unmapped', 'change_untested']),
  id: z.string().uuid().optional(),
  label: z.string(),
  detail: z.string().max(500),
});
export type RiskCoverageGap = z.infer<typeof RiskCoverageGapSchema>;

export const MaintenanceWarningSchema = z.object({
  reviewId: z.string().uuid().optional(),
  recommendation: z.string().max(80),
  impactLevel: z.string().max(40).optional(),
  summary: z.string().max(500),
});
export type MaintenanceWarning = z.infer<typeof MaintenanceWarningSchema>;

export const RiskSelectionSummarySchema = z.object({
  riskLevel: z.enum(RISK_SELECTION_LEVELS),
  riskScore: z.number().min(0).max(100),
  confidence: z.enum(RISK_SELECTION_CONFIDENCE),
  reasons: z.array(RiskReasonSchema).max(20),
  requiredCount: z.number().int().nonnegative(),
  recommendedCount: z.number().int().nonnegative(),
  optionalCount: z.number().int().nonnegative(),
  excludedCount: z.number().int().nonnegative(),
  highCriticalCount: z.number().int().nonnegative(),
});
export type RiskSelectionSummary = z.infer<typeof RiskSelectionSummarySchema>;

export const RiskSelectionEvaluateResponseSchema = z.object({
  projectId: z.string().uuid(),
  context: z.enum(RISK_SELECTION_CONTEXTS),
  /** Advisory — never executed by this endpoint. */
  executed: z.literal(false),
  summary: RiskSelectionSummarySchema,
  items: z.array(RiskSelectionItemSchema).max(500),
  affectedFlows: z.array(z.object({ id: z.string().uuid(), name: z.string() })).max(200),
  affectedScenarios: z.array(z.object({ id: z.string().uuid(), name: z.string() })).max(200),
  affectedRequirements: z.array(z.object({ id: z.string().uuid(), key: z.string(), name: z.string() })).max(200),
  coverage: RiskCoverageDimensionsSchema,
  coverageGaps: z.array(RiskCoverageGapSchema).max(100),
  maintenanceWarnings: z.array(MaintenanceWarningSchema).max(20),
  /** Opaque signal inventory for explainability / tests. */
  signalsUsed: z.array(z.string()).max(40),
});
export type RiskSelectionEvaluateResponse = z.infer<typeof RiskSelectionEvaluateResponseSchema>;

/** Inputs for pure scoring — unit-testable without DB. */
export interface RiskScoreInput {
  scenarioRisk?: 'critical' | 'high' | 'medium' | 'low' | null;
  scenarioPriority?: 'critical' | 'high' | 'medium' | 'low' | null;
  impactLevel?: ImpactLevel | null;
  flakeRate?: number | null;
  failRate?: number | null;
  healingRate?: number | null;
  maintenanceOpen?: boolean;
  uncoveredRequirement?: boolean;
  tiaScore?: number | null;
  recentlyExecuted?: boolean | null;
}

export function scoreFromScenarioRisk(risk: RiskScoreInput['scenarioRisk']): number {
  if (risk === 'critical') return 90;
  if (risk === 'high') return 70;
  if (risk === 'medium') return 40;
  if (risk === 'low') return 15;
  return 0;
}

export function scoreFromImpactLevel(level: ImpactLevel | null | undefined): number {
  if (level === 'critical') return 90;
  if (level === 'high') return 70;
  if (level === 'medium') return 45;
  if (level === 'low') return 20;
  return 0;
}

/**
 * Deterministic 0–100 score. Each contributing signal is capped;
 * reasons must be attached by the caller for explainability.
 */
export function computeRiskScore(input: RiskScoreInput): number {
  const parts: number[] = [];
  const scenarioPart = Math.max(
    scoreFromScenarioRisk(input.scenarioRisk),
    scoreFromScenarioRisk(input.scenarioPriority ?? null),
  );
  if (scenarioPart > 0) parts.push(scenarioPart * 0.35);

  const impactPart = scoreFromImpactLevel(input.impactLevel);
  if (impactPart > 0) parts.push(impactPart * 0.3);

  if (typeof input.flakeRate === 'number' && input.flakeRate > 0) {
    parts.push(Math.min(100, input.flakeRate * 100) * 0.2);
  }
  if (typeof input.failRate === 'number' && input.failRate > 0) {
    parts.push(Math.min(100, input.failRate * 100) * 0.25);
  }
  if (typeof input.healingRate === 'number' && input.healingRate > 0.15) {
    parts.push(Math.min(100, input.healingRate * 100) * 0.15);
  }
  if (input.maintenanceOpen) parts.push(55 * 0.2);
  if (input.uncoveredRequirement) parts.push(50 * 0.4);
  if (typeof input.tiaScore === 'number' && input.tiaScore > 0) {
    parts.push(input.tiaScore * 100 * 0.25);
  }
  if (input.recentlyExecuted === false) parts.push(25 * 0.1);

  if (parts.length === 0) return 0;
  const raw = parts.reduce((a, b) => a + b, 0);
  return Math.max(0, Math.min(100, Math.round(raw)));
}

export function riskLevelFromScore(score: number): RiskSelectionLevel {
  if (score >= 80) return 'critical';
  if (score >= 55) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

export function confidenceFromSignalCount(signalCount: number): RiskSelectionConfidence {
  if (signalCount >= 4) return 'high';
  if (signalCount >= 2) return 'medium';
  return 'low';
}

export function bucketFromScore(opts: {
  score: number;
  flakeRate?: number | null;
  excludeLowValue?: boolean;
}): RiskSelectionBucket {
  if (opts.excludeLowValue) return 'excluded';
  if (typeof opts.flakeRate === 'number' && opts.flakeRate >= 0.25 && opts.score < 55) {
    return 'excluded';
  }
  if (opts.score >= 70) return 'required';
  if (opts.score >= 40) return 'recommended';
  if (opts.score >= 15) return 'optional';
  return 'excluded';
}

export function coverageDimension(covered: number | null, total: number | null, note?: string): CoverageDimension {
  if (total == null || total <= 0 || covered == null) {
    return { ratio: null, covered: null, total: total ?? null, unknown: true, note };
  }
  return {
    ratio: Math.max(0, Math.min(1, covered / total)),
    covered,
    total,
    unknown: false,
    note,
  };
}
